import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { useLang } from '../lib/lang';
import { getUnreadCount, getSettings } from '../lib/api';
import { useBranding } from '../lib/branding';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu';
import { PasswordlessLoginModal } from './PasswordlessLogin';
import JoinModal from './JoinModal';
import { Menu, Wallet, LogOut, User, Shield, X, Globe, Bell, Sparkles, Pencil, PencilOff, Crown } from 'lucide-react';

const LOGO_URL_DEFAULT = '/assets/winluxor-logo.png';
const ICON_URL_DEFAULT = '/assets/winluxor-icon.png';

export default function Header() {
  const { user, isAdmin, logout } = useAuth();
  const { t, lang, switchLang, editMode, toggleEditMode } = useLang();
  const branding = useBranding();
  const LOGO_URL = branding.logo_main_url || LOGO_URL_DEFAULT;
  const ICON_URL = branding.logo_small_url || ICON_URL_DEFAULT;
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [unreadNotifs, setUnreadNotifs] = useState(0);
  const [navFlags, setNavFlags] = useState({});
  // logoVisible: whether the big top-of-page logo is currently in viewport
  // (controlled by IntersectionObserver, NOT by scroll position → no jitter).
  const [logoVisible, setLogoVisible] = useState(true);
  const logoSentinelRef = useRef(null);
  const [loginOpen, setLoginOpen] = useState(false);
  const [joinOpen, setJoinOpen] = useState(false);

  useEffect(() => {
    getSettings().then(r => {
      const ff = r.data.settings?.feature_flags || {};
      setNavFlags(ff);
    }).catch(() => {});
    if (user) {
      getUnreadCount().then(r => setUnreadNotifs(r.data.count || 0)).catch(() => {});
      const interval = setInterval(() => {
        getUnreadCount().then(r => setUnreadNotifs(r.data.count || 0)).catch(() => {});
      }, 30000);
      return () => clearInterval(interval);
    }
  }, [user]);

  // Observe the logo sentinel to know whether the logo is in view.
  // No scroll listener → no oscillation/jitter.
  useEffect(() => {
    const sentinel = logoSentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        setLogoVisible(entry.isIntersecting);
      },
      { rootMargin: '-30px 0px 0px 0px', threshold: 0 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <>
      {/* Big logo block — NOT sticky. Scrolls away naturally on scroll. */}
      <div className="bg-rolex-green border-b border-[hsl(var(--gold)/0.18)] py-3 sm:py-5" data-testid="site-logo-block">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-center">
          <Link to="/" data-testid="site-logo-link" className="transition-transform duration-300 hover:scale-[1.03] active:scale-[0.99]">
            <img
              src={LOGO_URL}
              alt="WINLUXOR"
              className="h-20 sm:h-24 lg:h-32 xl:h-36 w-auto rounded-lg shadow-[0_8px_32px_rgba(0,0,0,0.3)]"
            />
          </Link>
        </div>
        {/* Sentinel: tiny element below the logo we observe to know if logo is on-screen */}
        <div ref={logoSentinelRef} aria-hidden="true" className="h-px w-px" />
      </div>

      {/* Sticky nav row — fixed height, never resizes. Pure glassmorphism. */}
      <header
        className="sticky top-0 z-50 w-full nav-glass border-b border-[hsl(var(--gold)/0.30)] shadow-[0_2px_10px_rgba(0,0,0,0.15)]"
        data-testid="site-header"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-3 min-w-0">
              {/* Mini WL icon, fades in when the big logo leaves the viewport */}
              <Link
                to="/"
                className={`shrink-0 transition-[opacity,width,max-width] duration-300 ease-out ${
                  logoVisible ? 'opacity-0 w-0 max-w-0 overflow-hidden pointer-events-none' : 'opacity-100 w-10 max-w-[44px]'
                }`}
                aria-label="WINLUXOR Home"
              >
                <img
                  src={ICON_URL}
                  alt="WINLUXOR"
                  className="h-9 w-9 rounded-md object-cover shadow-[0_2px_8px_rgba(0,0,0,0.35)] ring-1 ring-[hsl(var(--gold)/0.45)]"
                />
              </Link>
              <nav className="hidden lg:flex items-center gap-7">
                <Link to="/" className="text-[13px] font-semibold tracking-[0.05em] text-cream/85 hover:text-gold transition-colors uppercase" data-testid="header-nav-home-link">{t('home')}</Link>
                {navFlags.nav_competitions !== false && <Link to="/#raffles" className="text-[13px] font-semibold tracking-[0.05em] text-cream/85 hover:text-gold transition-colors uppercase" data-testid="header-nav-competitions-link">{t('draws')}</Link>}
                {navFlags.nav_how_it_works !== false && <Link to="/#how-it-works" className="text-[13px] font-semibold tracking-[0.05em] text-cream/85 hover:text-gold transition-colors uppercase">{t('howItWorks')}</Link>}
                {navFlags.nav_winners !== false && <Link to="/winners" className="text-[13px] font-semibold tracking-[0.05em] text-cream/85 hover:text-gold transition-colors uppercase" data-testid="header-nav-winners-link">{t('recentWinners')}</Link>}
                {navFlags.nav_vip !== false && (
                  <Link to="/vip" className="text-[13px] font-bold tracking-[0.06em] text-gold hover:text-[hsl(var(--gold-light))] transition-colors flex items-center gap-1.5 uppercase">
                    <Sparkles className="w-3.5 h-3.5" />{t('vip')}
                  </Link>
                )}
              </nav>
            </div>

            <div className="hidden lg:flex items-center gap-2">
              {isAdmin && (
                <Button
                  variant={editMode ? 'default' : 'ghost'}
                  size="sm"
                  onClick={toggleEditMode}
                  className={editMode
                    ? 'gap-1.5 bg-gold-gradient text-rolex-green hover:opacity-90 font-bold animate-pulse'
                    : 'gap-1.5 text-cream/70 hover:text-gold hover:bg-[hsl(var(--gold)/0.08)]'}
                  title={editMode ? 'Désactiver l\u2019édition inline' : 'Activer l\u2019édition inline (admin)'}
                  data-testid="edit-mode-toggle"
                >
                  {editMode ? <PencilOff className="w-4 h-4" /> : <Pencil className="w-4 h-4" />}
                  <span className="text-xs uppercase tracking-wide">{editMode ? 'Édition ON' : 'Édition'}</span>
                </Button>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="gap-1.5 text-cream/70 hover:text-gold hover:bg-[hsl(var(--gold)/0.08)]" data-testid="lang-selector">
                    <Globe className="w-4 h-4" /><span className="text-xs font-semibold uppercase">{lang}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => switchLang('en')} className={lang === 'en' ? 'bg-accent' : ''}>EN English</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => switchLang('fr')} className={lang === 'fr' ? 'bg-accent' : ''}>FR Français</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              {user ? (
                <>
                  {user.vip_status && (
                    <Link to="/account" className="hidden xl:block" title="VIP Active">
                      <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gold-gradient shadow-md ring-1 ring-rolex-green/30 hover:scale-105 transition-transform" data-testid="header-vip-badge">
                        <Crown className="w-3.5 h-3.5 text-rolex-green" strokeWidth={2.5} />
                        <span className="text-[10px] font-extrabold tracking-[0.2em] uppercase text-rolex-green">VIP</span>
                      </div>
                    </Link>
                  )}
                  <Link to="/wallet" data-testid="header-wallet-button">
                    <Button variant="outline" size="sm" className="gap-2 border-[hsl(var(--gold)/0.4)] hover:border-gold bg-transparent text-gold hover:bg-[hsl(var(--gold)/0.08)] font-mono">
                      <Wallet className="w-4 h-4" />£{user.points_balance || 0}
                    </Button>
                  </Link>
                  <Link to="/account">
                    <Button variant="ghost" size="sm" className="gap-1 relative text-cream/70 hover:text-gold hover:bg-[hsl(var(--gold)/0.08)]">
                      <Bell className="w-4 h-4" />
                      {unreadNotifs > 0 && <span className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center font-mono">{unreadNotifs}</span>}
                    </Button>
                  </Link>
                  <Link to="/account"><Button variant="ghost" size="sm" className="gap-2 text-cream/85 hover:text-gold hover:bg-[hsl(var(--gold)/0.08)]"><User className="w-4 h-4" />{user.first_name}</Button></Link>
                  {isAdmin && <Link to="/admin"><Button variant="ghost" size="sm" className="gap-2 text-gold hover:bg-[hsl(var(--gold)/0.08)]"><Shield className="w-4 h-4" />{t('admin')}</Button></Link>}
                  <Button variant="ghost" size="icon" onClick={handleLogout} className="text-cream/50 hover:text-gold hover:bg-[hsl(var(--gold)/0.08)]"><LogOut className="w-4 h-4" /></Button>
                </>
              ) : (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setLoginOpen(true)}
                    className="text-cream/85 hover:text-gold hover:bg-[hsl(var(--gold)/0.08)] font-semibold tracking-wide gap-1.5"
                    data-testid="header-login-button"
                  >
                    <User className="w-4 h-4" />{t('signIn')}
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => setJoinOpen(true)}
                    className="btn-gold btn-press rounded-lg px-5 tracking-wide"
                    data-testid="header-join-button"
                  >
                    {t('joinNow')}
                  </Button>
                </>
              )}
            </div>

            <div className="flex lg:hidden items-center gap-2 ml-auto">
              <DropdownMenu>
                <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="text-cream/80"><Globe className="w-4 h-4" /></Button></DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => switchLang('en')}>EN</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => switchLang('fr')}>FR</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Sheet open={open} onOpenChange={setOpen}>
                <SheetTrigger asChild><Button variant="ghost" size="icon" className="text-cream" data-testid="mobile-nav-open-button"><Menu className="w-5 h-5" /></Button></SheetTrigger>
                <SheetContent side="right" className="w-80 max-w-[88vw] bg-rolex-green border-l border-[hsl(var(--gold)/0.25)] p-0" style={{ color: 'hsl(43 38% 92%)' }}>
                  <div className="flex flex-col h-full" style={{ color: 'hsl(43 38% 92%)' }}>
                    <div className="flex items-center justify-between p-4 border-b border-[hsl(var(--gold)/0.2)]">
                      <img src={LOGO_URL} alt="WINLUXOR" className="h-16 sm:h-20 rounded-md" />
                      <Button variant="ghost" size="icon" onClick={() => setOpen(false)} style={{ color: 'hsl(43 38% 92%)' }}><X className="w-5 h-5" /></Button>
                    </div>
                    <nav className="flex flex-col gap-1 p-4">
                      <Link to="/" onClick={() => setOpen(false)} style={{ color: 'hsl(43 38% 92%)' }} className="px-3 py-3 rounded-lg text-sm font-semibold uppercase tracking-wider hover:bg-[hsl(var(--gold)/0.10)] hover:!text-gold transition-colors">{t('home')}</Link>
                      {navFlags.nav_competitions !== false && <Link to="/#raffles" onClick={() => setOpen(false)} style={{ color: 'hsl(43 38% 92%)' }} className="px-3 py-3 rounded-lg text-sm font-semibold uppercase tracking-wider hover:bg-[hsl(var(--gold)/0.10)] hover:!text-gold transition-colors">{t('draws')}</Link>}
                      {navFlags.nav_how_it_works !== false && <Link to="/#how-it-works" onClick={() => setOpen(false)} style={{ color: 'hsl(43 38% 92%)' }} className="px-3 py-3 rounded-lg text-sm font-semibold uppercase tracking-wider hover:bg-[hsl(var(--gold)/0.10)] hover:!text-gold transition-colors">{t('howItWorks')}</Link>}
                      {navFlags.nav_winners !== false && <Link to="/winners" onClick={() => setOpen(false)} style={{ color: 'hsl(43 38% 92%)' }} className="px-3 py-3 rounded-lg text-sm font-semibold uppercase tracking-wider hover:bg-[hsl(var(--gold)/0.10)] hover:!text-gold transition-colors">{t('recentWinners')}</Link>}
                      {navFlags.nav_vip !== false && <Link to="/vip" onClick={() => setOpen(false)} className="px-3 py-3 rounded-lg text-sm font-bold uppercase tracking-wider !text-gold hover:bg-[hsl(var(--gold)/0.10)] transition-colors flex items-center gap-2"><Sparkles className="w-3.5 h-3.5" />{t('vip')}</Link>}
                    </nav>
                    <div className="mt-auto p-4 border-t border-[hsl(var(--gold)/0.2)] space-y-2">
                      {user ? (
                        <>
                          <Link to="/wallet" onClick={() => setOpen(false)}><Button variant="outline" className="w-full gap-2 border-[hsl(var(--gold)/0.5)] !text-gold bg-transparent hover:bg-[hsl(var(--gold)/0.08)]"><Wallet className="w-4 h-4" /> £{user.points_balance || 0}</Button></Link>
                          <Link to="/account" onClick={() => setOpen(false)}><Button variant="ghost" style={{ color: 'hsl(43 38% 92%)' }} className="w-full gap-2 mt-2 hover:bg-[hsl(var(--gold)/0.08)]"><User className="w-4 h-4" /> {t('myAccount')}</Button></Link>
                          {isAdmin && <Link to="/admin" onClick={() => setOpen(false)}><Button variant="ghost" className="w-full gap-2 mt-2 !text-gold hover:bg-[hsl(var(--gold)/0.08)]"><Shield className="w-4 h-4" /> {t('adminPanel')}</Button></Link>}
                          <Button variant="ghost" style={{ color: 'hsl(43 28% 78%)' }} className="w-full gap-2 mt-2 hover:bg-[hsl(var(--gold)/0.08)]" onClick={() => { handleLogout(); setOpen(false); }}><LogOut className="w-4 h-4" /> {t('signOut')}</Button>
                        </>
                      ) : (
                        <>
                          <Button onClick={() => { setOpen(false); setLoginOpen(true); }} variant="outline" className="w-full border-[hsl(var(--gold)/0.5)] !text-gold bg-transparent hover:bg-[hsl(var(--gold)/0.08)] gap-2" data-testid="mobile-login-button"><User className="w-4 h-4" /> {t('signIn')}</Button>
                          <Button onClick={() => { setOpen(false); setJoinOpen(true); }} className="w-full mt-2 btn-gold" data-testid="mobile-join-button">{t('joinNow')}</Button>
                        </>
                      )}
                    </div>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </header>
      <PasswordlessLoginModal open={loginOpen} onOpenChange={setLoginOpen} onSuccess={() => navigate('/account')} />
      <JoinModal open={joinOpen} onOpenChange={setJoinOpen} onSuccess={() => navigate('/account')} />
    </>
  );
}
